import { Editor, EditorContent, JSONContent, useEditor } from '@tiptap/react';
import { Paperclip } from 'lucide-react';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Button } from 'ui';
import { cn } from 'utils/cn';
// import LinkPreviewComponent from './components/LinkPreviewComponent';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import EditorBubbleMenu from './EditorBubbleMenu';
import DefaultToolbar from './EditorToolbar';
import baseExtensions from './extensions';
import CustomKeyboardShortcuts from './extensions/CustomKeyboardShortcuts';
import { LinkPreviewExtension } from './extensions/LinkPreview';
import suggestion from './extensions/suggestion';

export interface AtarimEditorProps {
  id?: string;
  content?: string;
  editable?: boolean;
  className?: string;
  placeholder?: string;
  editorClassName?: string;
  onSubmit?: () => void;
  onFileUpload?: (files: FileList) => Promise<void>;
  showLinkPreview?: boolean;
  linkPreviewComponent?: LinkPreviewComponent;
  showToolbar?: boolean;
  showBubbleMenu?: boolean;
  customExtensions?: any[];
  toolbar?: ToolbarComponent;
  bubbleMenu?: BubbleMenuComponent;
}

export interface PreviewState {
  show: boolean;
  url: string;
  x: number;
  y: number;
}

export type LinkPreviewComponent = React.ComponentType<LinkPreviewProps>;

export type LinkPreviewProps = {
  previewState: PreviewState;
  onOpenChange: (open: boolean) => void;
};

export type ToolbarProps = {
  editor: Editor | null;
};

export type ToolbarComponent = React.ComponentType<ToolbarProps>;

export type BubbleMenuComponent = React.ComponentType<BubbleMenuProps>;

export type BubbleMenuProps = {
  editor: Editor | null;
};

export interface AtarimEditorRef {
  editor: Editor | null;
  getHTML: () => string;
  getText: () => string;
  getJSON: () => JSONContent | null;
  focus: (options?: { preventScroll?: boolean }) => void;
}

const AtarimEditor = forwardRef<AtarimEditorRef, AtarimEditorProps>(
  (
    {
      id = '',
      content = '',
      editable = true,
      className = '',
      editorClassName = '',
      placeholder = '',
      onSubmit,
      onFileUpload,
      showLinkPreview = true,
      showBubbleMenu = false,
      showToolbar = true,
      toolbar: Toolbar,
      linkPreviewComponent: LinkPreview,
      bubbleMenu: BubbleMenu,
      customExtensions = [],
    },
    ref: ForwardedRef<AtarimEditorRef>
  ) => {
    const [preview, setPreview] = useState<PreviewState>({
      show: false,
      url: '',
      x: 0,
      y: 0,
    });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const onSubmitRef = useRef(onSubmit);

    const handleFileInputChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !onFileUpload) return;
        await onFileUpload(files);
      },
      [onFileUpload]
    );

    useEffect(() => {
      onSubmitRef.current = onSubmit;
    }, [onSubmit]);

    // Handle all keydown events to prevent any global shortcuts from interfering
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Check if the event target is within the editor
        if (editorContainerRef.current?.contains(e.target as Node)) {
          // Stop propagation for ALL keys when focused in the editor
          // This prevents any website-specific shortcuts from interfering
          e.stopPropagation();
        }
      };

      const handleKeyUp = (e: KeyboardEvent) => {
        // Also handle keyup events to be thorough
        if (editorContainerRef.current?.contains(e.target as Node)) {
          e.stopPropagation();
        }
      };

      const handleKeyPress = (e: KeyboardEvent) => {
        // Handle keypress events as well for complete coverage
        if (editorContainerRef.current?.contains(e.target as Node)) {
          e.stopPropagation();
        }
      };

      // Add event listeners with capture=true to intercept before global handlers
      document.addEventListener('keydown', handleKeyDown, true);
      document.addEventListener('keyup', handleKeyUp, true);
      document.addEventListener('keypress', handleKeyPress, true);

      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
        document.removeEventListener('keyup', handleKeyUp, true);
        document.removeEventListener('keypress', handleKeyPress, true);
      };
    }, []);

    const extensions = [
      ...baseExtensions,
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: suggestion,
      }),
      CustomKeyboardShortcuts.configure({
        onSubmit: () => onSubmitRef.current?.(),
      }),
      Placeholder.configure({ placeholder: placeholder ? placeholder : 'Write your message...' }),
      ...customExtensions,
    ];

    if (showLinkPreview) {
      extensions.push(
        LinkPreviewExtension.configure({
          onShowPreview: (url, rect) => {
            setPreview({
              show: true,
              url,
              x: rect.left + rect.width / 2,
              y: rect.bottom,
            });
          },
          onHidePreview: () => {
            setPreview((prev) => ({ ...prev, show: false }));
          },
        })
      );
    }

    const editor = useEditor({
      editable,
      extensions,
      content,
      editorProps: {
        attributes: {
          class: cn(
            'relative prose dark:prose-invert dark:text-textcolor prose-code:text-primary-foreground prose-sm max-w-none w-full word-break prose-code:text-sm prose-code:font-normal prose-pre:bg-gray-100 prose-p:my-0 [&>p>a]:text-textcolor',
            editable &&
            'min-h-[150px] max-h-[400px] rounded-br-md rounded-bl-md border overflow-y-auto p-2 ',
            editorClassName
          ),
        },
        // Additional editor props to handle all events
        handleKeyDown: (view, event) => {
          // Stop propagation for all keys within the editor
          event.stopPropagation();
          return false; // Let TipTap handle the event normally
        },
        handleKeyPress: (view, event) => {
          // Stop propagation for all keypress events within the editor
          event.stopPropagation();
          return false; // Let TipTap handle the event normally
        },
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        editor,
        getHTML: () => editor?.getHTML() || '',
        getText: () => editor?.getText() || '',
        getJSON: () => editor?.getJSON() || null,
        focus: (options?: { preventScroll?: boolean }) => {
          editor?.commands.focus(undefined, { scrollIntoView: false });
        },
      }),
      [editor]
    );

    useEffect(() => {
      if (editor) {
        editor.commands.setContent(content);
      }
    }, [editor, content]);

    return (
      <div
        ref={editorContainerRef}
        className={cn('relative', editable && 'mt-2', className)}
        onKeyDown={(e) => {
          // Additional safety net - stop all key event propagation at the container level
          e.stopPropagation();
        }}
        onKeyUp={(e) => {
          // Stop keyup propagation as well
          e.stopPropagation();
        }}
        onKeyPress={(e) => {
          // Stop keypress propagation for complete coverage
          e.stopPropagation();
        }}
      >
        {editable && showToolbar && (
          <>
            {Toolbar ?
              <Toolbar editor={editor} /> :
              <DefaultToolbar editor={editor} />}
          </>
        )}
        <EditorContent editor={editor} id={id} />
        {showLinkPreview && LinkPreview && (
          <LinkPreview
            previewState={preview}
            onOpenChange={(open) =>
              setPreview((prev) => ({ ...prev, show: open }))
            }
          />
        )}
        {showBubbleMenu && editor && (
          <>
            {BubbleMenu ?
              <BubbleMenu editor={editor} /> :
              <EditorBubbleMenu editor={editor} />
            }
          </>
        )}
        {onFileUpload && (
          <>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="ghost"
              size="icon"
              tooltip="Add attachments to this task"
              className={cn(
                'absolute bottom-2 right-2 h-10 w-10 text-gray-300',
                'flex items-center justify-center bg-transparent hover:bg-transparent',
                'transition-colors duration-200 hover:text-gray-600'
              )}
            >
              <Paperclip size={20} />
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              className="hidden"
              multiple
            />
          </>
        )}
      </div>
    );
  }
);

AtarimEditor.displayName = 'AtarimEditor';

export default AtarimEditor;